import {
  useEffect,
  useMemo,
  useRef,
  useState,
  ChangeEvent,
  ComponentType,
} from "react";
import { flagEmoji } from "../../../../helper/common";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PublicIcon from "@mui/icons-material/Public";

interface SelectOption {
  value: string;
  labelKey: string;
}

interface CountryOption extends SelectOption {
  name: string;
  flag: string;
}

interface DocMetaEntry {
  title: string;
  hint: string;
  Icon: ComponentType<{ className?: string }>;
}

const DOC_META: Record<string, DocMetaEntry> = {
  national_identity_card: {
    title: "National identity card",
    hint: "Front and back",
    Icon: BadgeOutlinedIcon,
  },
  passport: {
    title: "Passport",
    hint: "Photo page",
    Icon: PublicIcon,
  },
  residence_permit: {
    title: "Residence permit",
    hint: "Front and back",
    Icon: HomeWorkOutlinedIcon,
  },
};

type TranslateFn = (
  key: string,
  context?: string,
  namespace?: string
) => string;

interface DocumentSelectScreenProps {
  issuingCountry: string;
  docType: string;
  availableCountries: SelectOption[];
  availableDocs: SelectOption[];
  onCountryChange: (country: string) => void;
  onDocTypeChange: (docType: string) => void;
  onContinue: (docType?: string) => void;
  showRetryMessage: boolean;
  _t: TranslateFn;
}

const formatLabel = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getTranslatedText = (
  _t: TranslateFn,
  key: string,
  fallback: string
): string => {
  const translated = _t(key);
  return translated === key ? fallback : translated;
};

const DocumentSelectScreen = ({
  issuingCountry,
  docType,
  availableCountries,
  availableDocs,
  onCountryChange,
  onDocTypeChange,
  onContinue,
  showRetryMessage,
  _t,
}: DocumentSelectScreenProps) => {
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const countryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!issuingCountry) {
      countryInputRef.current?.focus();
    }
  }, []);

  const countryOptions: CountryOption[] = useMemo(
    () =>
      availableCountries
        .map((country) => ({
          ...country,
          name: getTranslatedText(
            _t,
            country.labelKey,
            formatLabel(country.labelKey)
          ),
          flag: flagEmoji(country.value),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [availableCountries, _t]
  );

  const selectedCountry = countryOptions.find(
    (country) => country.value === issuingCountry
  );

  const filteredCountries = countryOptions.filter((country) =>
    country.name.toLowerCase().includes(countrySearch.trim().toLowerCase())
  );

  const handleCountrySelect = (countryValue: string) => {
    onCountryChange(countryValue);
    setCountrySearch("");
    setIsCountryOpen(false);
  };

  const handleCountryFieldClick = () => {
    setCountrySearch(selectedCountry?.name || "");
    setIsCountryOpen(true);
  };

  const handleCountryClear = () => {
    onCountryChange("");
    setCountrySearch("");
    setIsCountryOpen(true);
  };

  const handleDocumentSelect = (documentValue: string) => {
    onDocTypeChange(documentValue);
    onContinue(documentValue);
  };

  return (
    <div className="custom-kyc-body custom-kyc-document-body">
      <div className="custom-kyc-document-intro">
        <h3>{_t("Choose your document")}</h3>
        <p>{_t("Select issuing country to see which documents we accept")}</p>
      </div>

      {showRetryMessage && (
        <div className="custom-kyc-document-retry-message" role="alert">
          {_t(
            "The previously uploaded documents were not accepted. Please try again with new documents."
          )}
        </div>
      )}

      <div className="custom-kyc-document-form">
        <div className="custom-kyc-country-field">
          <label>{_t("Issuing country")}</label>
          <div
            className={`custom-kyc-country-combobox ${
              isCountryOpen ? "open" : ""
            }`}
          >
            <div className="custom-kyc-country-input-wrap">
              {!isCountryOpen && selectedCountry?.flag && (
                <span className="custom-kyc-country-selected-flag">
                  {selectedCountry.flag}
                </span>
              )}
              <input
                ref={countryInputRef}
                type="text"
                value={
                  isCountryOpen ? countrySearch : selectedCountry?.name || ""
                }
                onChange={(event) => {
                  setCountrySearch(event.target.value);
                  setIsCountryOpen(true);
                }}
                onClick={handleCountryFieldClick}
                onFocus={handleCountryFieldClick}
                placeholder={_t("Select issuing country")}
                className={`custom-kyc-country-input ${
                  !isCountryOpen && selectedCountry?.flag ? "has-flag" : ""
                }`}
              />
              {isCountryOpen && countrySearch ? (
                <button
                  type="button"
                  className="custom-kyc-country-icon-btn"
                  onClick={handleCountryClear}
                  aria-label={_t("Clear")}
                >
                  <CloseRoundedIcon />
                </button>
              ) : (
                <button
                  type="button"
                  className="custom-kyc-country-icon-btn"
                  onClick={() => {
                    setCountrySearch(selectedCountry?.name || "");
                    setIsCountryOpen((current) => !current);
                  }}
                  aria-label={_t("Toggle country list")}
                >
                  {isCountryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
              )}
            </div>

            {isCountryOpen && (
              <div className="custom-kyc-country-options" role="listbox">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      type="button"
                      className="custom-kyc-country-option"
                      key={country.value}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleCountrySelect(country.value)}
                    >
                      {country.flag && (
                        <span className="custom-kyc-country-flag">
                          {country.flag}
                        </span>
                      )}
                      <span>{country.name}</span>
                    </button>
                  ))
                ) : (
                  <div className="custom-kyc-country-empty">
                    {_t("No countries found")}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {selectedCountry && (
          <div className="custom-kyc-documents-section">
            <h4>{_t("Accepted documents")}</h4>
            <div className="custom-kyc-document-list">
              {availableDocs.map((document) => {
                const meta = DOC_META[document.value] || {
                  title: formatLabel(document.labelKey),
                  hint: "Front and back",
                  Icon: BadgeOutlinedIcon,
                };
                const Icon = meta.Icon;

                return (
                  <button
                    type="button"
                    className={`custom-kyc-document-card ${
                      docType === document.value ? "active" : ""
                    }`}
                    key={document.value}
                    onClick={() => handleDocumentSelect(document.value)}
                  >
                    <Icon className="custom-kyc-document-card-icon" />
                    <span className="custom-kyc-document-card-copy">
                      <strong>
                        {getTranslatedText(_t, document.labelKey, meta.title)}
                      </strong>
                      <span>{_t(meta.hint)}</span>
                    </span>
                    <KeyboardArrowRightIcon className="custom-kyc-document-card-arrow" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentSelectScreen;
